import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, Navigate } from 'react-router-dom';

// Import thunks from your auth slice (same pattern you used in OauthCallback)
const authActions = require('../../features/auth/authSlice');

const fetchOpenIdConfiguration = async (issuerUri) => {
  const wellKnown = issuerUri.replace(/\/+$/, '') + '/.well-known/openid-configuration';
  const res = await fetch(wellKnown, { cache: 'no-store' });
  if (!res.ok) throw new Error('Discovery failed: ' + res.status);
  return res.json();
};

export const OpenIDCallback = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const authenticating = useRef(false);

  const authState = useSelector((state) => state.auth);

  // Parse query
  const query = new URLSearchParams(location.search);
  const code  = query.get('code');
  const state = query.get('state');
  const err   = query.get('error');
  const errDesc = query.get('error_description');

  useEffect(() => {
    const run = async () => {
      if (authenticating.current) return;
      if (authState.isLoggedIn) return;
      if (err) {
        console.error('OIDC error:', err, errDesc);
        dispatch(authActions.exchangeCodeForTokenDynamic.rejected());
        return;
      }
      if (!code) return;

      // Retrieve context stored during "Login with SSO" click
      // We store a JSON blob with the country and OIDC params.
      const ctxRaw = sessionStorage.getItem('pkce:context');
      if (!ctxRaw) {
        console.error('Missing PKCE context. Cannot complete login.');
        dispatch(authActions.exchangeCodeForTokenDynamic.rejected());
        return;
      }

      const ctx = JSON.parse(ctxRaw);
      const { country, issuerUri, clientId, redirectUri } = ctx || {};

      // Validate stored state & load code_verifier by country prefix
      const keyPrefix = `pkce:${(country || 'xx').toLowerCase()}`;
      const storedState = sessionStorage.getItem(`${keyPrefix}:state`);
      const codeVerifier = sessionStorage.getItem(`${keyPrefix}:code_verifier`);

      if (!storedState || storedState !== state) {
        console.error('State mismatch or missing; aborting.');
        dispatch(authActions.exchangeCodeForTokenDynamic.rejected());
        return;
      }
      if (!codeVerifier) {
        console.error('Missing PKCE code_verifier; aborting.');
        dispatch(authActions.exchangeCodeForTokenDynamic.rejected());
        return;
      }

      // Resolve token endpoint (use cached if we saved it)
      let tokenEndpoint = sessionStorage.getItem(`${keyPrefix}:token_endpoint`);
      if (!tokenEndpoint) {
        try {
          const cfg = await fetchOpenIdConfiguration(issuerUri);
          tokenEndpoint = cfg.token_endpoint;
          sessionStorage.setItem(`${keyPrefix}:token_endpoint`, tokenEndpoint);
        } catch (e) {
          console.error('Failed to discover token endpoint', e);
          dispatch(authActions.exchangeCodeForTokenDynamic.rejected());
          return;
        }
      }

      authenticating.current = true;

      // Dispatch dynamic exchange (no client secret; PKCE instead)
      dispatch(
        authActions.exchangeCodeForTokenDynamic({
          tokenEndpoint,
          code,
          codeVerifier,
          clientId,
          redirectUri
        })
      ).finally(() => {
        // Always clear one-time PKCE artifacts
        sessionStorage.removeItem(`${keyPrefix}:state`);
        sessionStorage.removeItem(`${keyPrefix}:code_verifier`);
        // keep token_endpoint cached for the session
      });
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, state, err, errDesc]);

  if (authState.isLoggedIn) return <Navigate to="/" replace />;
  if (authState.loading) return <h1>Signing you in…</h1>;
  if (authState.error) return <h1>Login failed. Please try again.</h1>;

  return <h1>Finishing sign-in…</h1>;
};
